import { store } from "../core/store";
import { socket } from "../core/socket";
import type { CCCChoicePayload, CCCOfferDTO, CCCOfferOption } from "../../shared/types";
import { formatBonusPercent } from "./cccTooltip";

export function mountCCCSelection(container: HTMLElement): () => void {
  const wrap = document.createElement("div");
  wrap.className = "ccc-wrap";

  let selectedIds: string[] = [];
  let hasSubmitted = false;
  let lastOffer: CCCOfferDTO | null = null;

  function maxSelectable(offer: CCCOfferDTO): number {
    return offer.situation === "DEBUG" ? 2 : 1;
  }

  function submitChoice(payload: CCCChoicePayload): void {
    hasSubmitted = true;
    render();
    socket.emit("ccc:choose", payload, (ok) => {
      if (!ok) {
        hasSubmitted = false;
        selectedIds = [];
        render();
      }
    });
  }

  /** Splits the leading action phrase ("Replace", "Upgrade", "Pick up") off an
   * option's label so it can be styled distinctly from the rest - only for
   * options that actually got one (an unavailable "No other cards available"/
   * "No upgrade available" label has no action word to pull out). */
  function splitActionLabel(option: CCCOfferOption): { action: string | null; rest: string } {
    if (option.available) {
      if (option.kind === "replace" && option.label.startsWith("Replace ")) {
        return { action: "Replace", rest: option.label.slice("Replace".length) };
      }
      if (option.kind === "upgrade" && option.label.startsWith("Upgrade ")) {
        return { action: "Upgrade", rest: option.label.slice("Upgrade".length) };
      }
      if (option.kind === "add" && option.label.startsWith("Pick up ")) {
        return { action: "Pick up", rest: option.label.slice("Pick up".length) };
      }
    }
    return { action: null, rest: option.label };
  }

  function buildOptionCard(option: CCCOfferOption): HTMLElement {
    const item = document.createElement("div");
    item.className = "ccc-option";

    if (option.kind === "replace" || option.kind === "upgrade" || option.kind === "add") {
      const kicker = document.createElement("div");
      kicker.className = "ccc-option-kicker";
      // Kicker itself is a flex container (for the bottom-align/centering
      // below), so it needs exactly ONE child here - splitting the action
      // word and the rest text directly into the flex container would turn
      // them into two independent flex items instead of one flowing
      // sentence, which both eats the space between them (flex items trim
      // their own leading whitespace) and wraps them as separate rigid boxes
      // instead of one paragraph. This inner span keeps them in normal
      // inline flow; the flex properties then apply to the span as a whole.
      const textWrap = document.createElement("span");
      const { action, rest } = splitActionLabel(option);
      if (action) {
        const actionSpan = document.createElement("span");
        actionSpan.className = "ccc-option-kicker-action";
        actionSpan.textContent = action;
        textWrap.append(actionSpan, document.createTextNode(rest));
      } else {
        textWrap.textContent = option.label;
      }
      kicker.appendChild(textWrap);
      item.appendChild(kicker);
    }

    const card = document.createElement("button");
    card.type = "button";
    card.className = "ccc-card";
    if (!option.available) card.classList.add("ccc-card--unavailable");
    if (selectedIds.includes(option.id)) card.classList.add("ccc-card--selected");
    card.disabled = !option.available;

    if (option.card) {
      const name = document.createElement("div");
      name.className = "ccc-card-name";
      name.textContent = option.card.name;
      const desc = document.createElement("div");
      desc.className = "ccc-card-desc";
      desc.textContent = option.card.description;
      const bonus = document.createElement("div");
      bonus.className = "ccc-card-bonus";
      bonus.textContent = `+${formatBonusPercent(option.card)}% drawing bonus points`;
      card.append(name, desc, bonus);
    } else {
      const placeholder = document.createElement("div");
      placeholder.className = "ccc-card-placeholder";
      placeholder.textContent = option.label;
      card.appendChild(placeholder);
    }

    card.addEventListener("click", () => {
      if (!option.available) return;
      const offer = store.getState().cccOffer;
      if (!offer) return;
      const max = maxSelectable(offer);
      const idx = selectedIds.indexOf(option.id);
      if (idx !== -1) {
        selectedIds = selectedIds.filter((id) => id !== option.id);
      } else if (max === 1) {
        selectedIds = [option.id];
      } else if (selectedIds.length < max) {
        selectedIds = [...selectedIds, option.id];
      }
      render();
    });

    item.appendChild(card);
    return item;
  }

  function render(): void {
    const offer = store.getState().cccOffer;
    wrap.innerHTML = "";

    if (!offer) {
      const waiting = document.createElement("p");
      waiting.className = "hint-text";
      waiting.textContent = "Waiting for the next phase...";
      wrap.appendChild(waiting);
      return;
    }

    if (offer !== lastOffer) {
      lastOffer = offer;
      selectedIds = [];
      hasSubmitted = false;
    }

    const heading = document.createElement("h2");
    heading.className = "ccc-heading";
    heading.textContent = "Choose a Chaos Canvas Card";
    wrap.appendChild(heading);

    if (hasSubmitted) {
      const waiting = document.createElement("p");
      waiting.className = "hint-text";
      waiting.textContent = "Waiting for other players to choose...";
      wrap.appendChild(waiting);
      return;
    }

    if (offer.currentInventory.length > 0) {
      const currentWrap = document.createElement("p");
      currentWrap.className = "hint-text ccc-current-inventory";
      currentWrap.textContent = `Your current cards: ${offer.currentInventory.map((c) => c.name).join(", ")}`;
      wrap.appendChild(currentWrap);
    }

    const grid = document.createElement("div");
    grid.className = "ccc-grid";
    for (const option of offer.options) {
      grid.appendChild(buildOptionCard(option));
    }
    wrap.appendChild(grid);

    const confirmBtn = document.createElement("button");
    confirmBtn.type = "button";
    confirmBtn.className = "primary-button ccc-confirm-button";
    confirmBtn.textContent = "Confirm Selection";
    confirmBtn.disabled = selectedIds.length === 0;
    confirmBtn.addEventListener("click", () => submitChoice({ choice: "select", optionIds: selectedIds }));
    wrap.appendChild(confirmBtn);

    const actionsRow = document.createElement("div");
    actionsRow.className = "ccc-actions-row";

    if (offer.currentInventory.length > 0) {
      const discardBtn = document.createElement("button");
      discardBtn.type = "button";
      discardBtn.className = "secondary-button";
      discardBtn.textContent = "Discard all of your Chaos Canvas Cards";
      discardBtn.addEventListener("click", () => submitChoice({ choice: "discard" }));
      actionsRow.appendChild(discardBtn);
    }

    const skipBtn = document.createElement("button");
    skipBtn.type = "button";
    skipBtn.className = "secondary-button";
    skipBtn.textContent = "Skip Selection >>>";
    skipBtn.addEventListener("click", () => submitChoice({ choice: "skip" }));
    actionsRow.appendChild(skipBtn);

    wrap.appendChild(actionsRow);
  }

  render();
  // Only the offer itself comes from the store (selection state is local and
  // re-renders directly). Rebuilding the cards on every unrelated update - each
  // chat message, every snapshot - could swallow a click that landed mid-rebuild.
  let renderedOffer = store.getState().cccOffer;
  const unsub = store.subscribe((state) => {
    if (state.cccOffer === renderedOffer) return;
    renderedOffer = state.cccOffer;
    render();
  });
  container.appendChild(wrap);
  return () => unsub();
}
